import { consume } from "@lit/context";
import {
    type DocumentDTO,
    type DocumentOrigin,
    DocumentTagDTO,
    type Form,
    MimeType,
    fileExtension,
    fileIcon,
    formStatusColor,
    formStatusLabel,
    formatDate,
    formatMonth,
    getDefaultForms,
    getRange,
    toDateString,
} from "@pentacode/core";
import "@pentacode/ui/components";
import { LitElement, css, html } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import "../../../components/documents/document-dialog";
import type { DocumentDialog } from "../../../components/documents/document-dialog";
import {
    documentMatchesOrigins,
    documentOriginMeta,
    documentOriginOptions,
} from "../../../components/documents/document-origin";
import "../../../components/documents/document-origin-pill";
import "../../../components/documents/edit-document-dialog";
import type { EditDocumentDialog } from "../../../components/documents/edit-document-dialog";
import { type EnabledFeatures, enabledFeatures } from "../../../contexts/features-context";
import { alert, confirm } from "../../../elements/alert-dialog";
import "../../../elements/popover";
import "../../../elements/scroller";
import { app } from "../../../init";
import { singleton } from "../../../lib/singleton";
import { downloadFromUrl, readFileAsDataURL, selectFile } from "../../../lib/util";
import { Routing, routeProperty } from "../../../mixins/routing";
import { StateMixin } from "../../../mixins/state";
import { shared } from "../../../styles";

@customElement("ptc-employees-documents-single")
export class EmployeesDocumentsSingle extends Routing(StateMixin(LitElement)) {
    routePattern = /^employees\/(?<employeeId>\d+)\/documents/;

    @routeProperty({ arg: "employeeId", type: Number })
    employeeId: number;

    @routeProperty({ param: "openDoc", type: Number })
    private _openDoc: number;

    get routeTitle() {
        return `Dokumente: ${this._employee && this._employee.name}`;
    }

    async handleRoute() {
        await this._load();

        if (this._openDoc) {
            const document = this._documents.find((d) => d.id === this._openDoc);
            if (document) {
                await this._openDocument(document);
            }
            this.go(null, { openDoc: undefined }, true);
        }
    }

    @state()
    private _loading = false;

    @state()
    private _documents: DocumentDTO[] = [];

    @state()
    private _months: {
        date: string;
        documents: DocumentDTO[];
    }[];

    @state()
    private _selectedTag: DocumentTagDTO | null = null;

    @state()
    private _documentTags: DocumentTagDTO[] = [];

    @state()
    private _activeOrigins: Set<DocumentOrigin> = new Set(documentOriginOptions);

    @consume({ context: enabledFeatures, subscribe: true })
    private _enabledFeatures?: EnabledFeatures;

    @singleton("ptc-document-dialog")
    private _documentDialog: DocumentDialog;

    @singleton("ptc-edit-document-dialog")
    private _editDocumentDialog: EditDocumentDialog;

    @state()
    private _filterString = "";

    @query("#filterInput")
    private _filterInput: HTMLInputElement;

    private get _employee() {
        return (this.employeeId && app.getEmployee(this.employeeId)) || null;
    }

    private _updateFilter() {
        this._filterString = this._filterInput.value.toLowerCase();
    }

    private _clearFilter() {
        this._filterString = this._filterInput.value = "";
    }

    // Returns true if the document matches the current filter criteria
    private _matchesFilters(document: DocumentDTO): boolean {
        const filterString = this._filterString.toLowerCase();
        const matchesOrigin = documentMatchesOrigins(document, this._activeOrigins);
        const matchesTag = !this._selectedTag || document.tags.some((tag) => tag.id === this._selectedTag?.id);
        const matchesName = document.name.toLowerCase().includes(filterString);
        const matchesTagName = document.tags.some((tag) => tag.name.toLowerCase().includes(filterString));

        return matchesOrigin && matchesTag && (matchesName || matchesTagName);
    }

    private _toggleOrigin(origin: DocumentOrigin) {
        const next = new Set(this._activeOrigins);
        if (next.has(origin)) {
            if (next.size === 1) {
                return;
            }
            next.delete(origin);
        } else {
            next.add(origin);
        }
        this._activeOrigins = next;
    }

    private async _load() {
        if (!this._employee) {
            return;
        }

        this._loading = true;

        try {
            const [documentsPromise, documentTagsPromise] = await Promise.allSettled([
                app.api.getDocuments({ employeeId: this.employeeId }),
                app.api.getDocumentTagsForCompany(),
            ]);
            this._documents = documentsPromise.status === "fulfilled" ? documentsPromise.value : [];
            this._documentTags = documentTagsPromise.status === "fulfilled" ? documentTagsPromise.value : [];

            this._documents.sort((a, b) => (a.date < b.date ? -1 : 1));

            const months: { date: string; documents: DocumentDTO[] }[] = [];

            for (const document of this._documents) {
                const date = getRange(document.date, "month").from;
                if (date === months[0]?.date) {
                    months[0].documents.push(document);
                } else {
                    months.unshift({ date, documents: [document] });
                }
            }

            this._months = months;
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    private async _newDocument({ file, form }: { file?: File; form?: Form } = {}) {
        const document = {
            employeeId: this.employeeId,
            date: toDateString(new Date()),
            tags: this._selectedTag ? [new DocumentTagDTO(this._selectedTag)] : [],
            form: form ?? null,
            name: form?.name || file?.name || "",
            type: form ? MimeType.PDF : (file?.type as MimeType),
            comment: "",
        };

        const created = await this._editDocumentDialog.show({ file, document });
        if (created) {
            this._load();
            app.fetchCompany();

            this._openDocument(created);
        }
    }

    private async _openDocument(document: DocumentDTO) {
        const edited = await this._documentDialog.show(document, { showActions: true });
        if (edited) {
            await this._load();

            // update in the background
            void app.fetchCompany();
        }
    }

    private _dragover(e: DragEvent) {
        e.preventDefault();

        if (e.dataTransfer?.files.length) {
            e.dataTransfer.dropEffect = "copy";
        }
    }

    private _drop(e: DragEvent) {
        e.preventDefault();

        const file = e.dataTransfer?.files[0];

        if (file) {
            void this._newDocument({ file });
        }
    }

    private async _edit(document: DocumentDTO) {
        const edited = await this._editDocumentDialog.show({ document });
        if (edited) {
            void this._load();
        }
    }

    private async _delete(document: DocumentDTO) {
        if (!document.id) {
            return;
        }

        const confirmed = await confirm(
            "Sind Sie sicher dass Sie dieses Dokument löschen wollen?",
            "Löschen",
            "Abbrechen",
            { title: "Dokument Löschen", type: "destructive" }
        );

        if (!confirmed) {
            return;
        }

        this._loading = true;

        try {
            await app.api.deleteDocument(document.id);
            await this._load();
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    private async _download(document: DocumentDTO) {
        if (!document.url) {
            return;
        }
        const res = await fetch(document.url);
        const data = await readFileAsDataURL(await res.blob());
        await downloadFromUrl(data, `${document.name}.${fileExtension(document.type)}`);
    }

    static styles = [
        shared,
        css`
            .document-row:not(:hover) .document-actions {
                display: none;
            }
        `,
    ];

    private _renderNewDocumentOptions() {
        return html`
            <ptc-popover class="popover-menu" hide-on-click>
                ${getDefaultForms(app.company!).map(
                    (form) => html`
                        <button type="button" class="small" @click=${() => this._newDocument({ form })}>
                            <ptc-ui-icon name="icon.object.form"></ptc-ui-icon> ${form.name}
                        </button>
                    `
                )}

                <button
                    type="button"
                    class="small"
                    @click=${async () => {
                        const file = await selectFile();
                        if (file) {
                            await this._newDocument({ file });
                        }
                    }}
                >
                    <ptc-ui-icon name="icon.action.choose-file"></ptc-ui-icon> Datei Wählen...
                </button>
            </ptc-popover>
        `;
    }

    render() {
        const emp = this._employee;
        if (!emp) {
            return;
        }

        return html`
            <div class="fullbleed vertical layout" @drop=${this._drop} @dragover=${this._dragover}>
                <div class="padded horizontal layout border-bottom">
                    <div class="small right icon input noprint">
                        <input id="filterInput" placeholder="Suchen..." @input=${this._updateFilter} />
                        ${this._filterString
                            ? html`<ptc-ui-icon
                                  name="icon.action.clear"
                                  class="click icon"
                                  @click=${this._clearFilter}
                              ></ptc-ui-icon>`
                            : html`<ptc-ui-icon
                                  name="icon.action.search"
                                  class="icon"
                                  @click=${this._clearFilter}
                              ></ptc-ui-icon>`}
                    </div>
                    <div class="stretch"></div>
                    <button class="large skinny transparent">
                        <ptc-ui-icon name="icon.action.upload"></ptc-ui-icon>
                    </button>
                    ${this._renderNewDocumentOptions()}
                </div>

                ${this._enabledFeatures?.documentOriginFilter
                    ? html`
                          <div class="small padded vertical layout border-bottom">
                              <div class="tiny subtle semibold bottom-margined center-aligning horizontal layout">
                                  <ptc-ui-icon name="icon.object.source" class="right-margined"></ptc-ui-icon> Quelle
                              </div>
                              <div class="spacing wrapping horizontal layout">
                                  ${documentOriginOptions.map(
                                      (origin) => html`
                                          <button
                                              class="slim ${this._activeOrigins.has(origin)
                                                  ? "primary"
                                                  : "transparent"}"
                                              @click=${() => this._toggleOrigin(origin)}
                                          >
                                              <ptc-ui-icon name="${documentOriginMeta[origin].icon}"></ptc-ui-icon>
                                              ${documentOriginMeta[origin].label}
                                          </button>
                                      `
                                  )}
                              </div>
                          </div>
                      `
                    : ""}

                <div class="small padded vertical layout border-bottom">
                    <div class="tiny subtle semibold bottom-margined center-aligning horizontal layout">
                        <ptc-ui-icon name="icon.object.category" class="right-margined"></ptc-ui-icon> Kategorien
                    </div>
                    <div class="spacing wrapping center-aligning horizontal layout">
                        <button
                            class="slim ${!this._selectedTag ? "primary" : "transparent"}"
                            @click=${() => (this._selectedTag = null)}
                        >
                            <ptc-ui-icon name="icon.object.category"></ptc-ui-icon> Alle
                            <span class="subtle" style="display: inline-block; margin-left: 0.25em;">
                                ${this._documents.length}
                            </span>
                        </button>
                        ${this._documentTags?.map(
                            (tag) => html`
                                <button
                                    class="slim ${this._selectedTag?.id === tag.id ? "primary" : "transparent"}"
                                    @click=${() => (this._selectedTag = tag)}
                                    style="--color-highlight: ${tag.color}"
                                >
                                    <ptc-ui-icon name="icon.object.tag"></ptc-ui-icon> ${tag.name}
                                    <span class="subtle" style="display: inline-block; margin-left: 0.25em;">
                                        ${this._documents.filter((doc) => doc.tags.some((t) => t.name === tag.name))
                                            .length}
                                    </span>
                                </button>
                            `
                        )}
                        <div class="stretch"></div>
                        <button
                            class="subtle"
                            @click=${() => this.go("settings/documents")}
                            ?disabled=${!app.hasPermission("manage.settings.documents")}
                        >
                            <ptc-ui-icon name="icon.action.edit"></ptc-ui-icon> Kategorien Bearbeiten
                        </button>
                    </div>
                </div>

                ${!this._documents.length
                    ? html`
                          <div class="stretch centering vertical layout">
                              <ptc-ui-icon name="icon.file.collection" class="giant faded"></ptc-ui-icon>
                              <div class="margined">Dieser Mitarbeiter hat noch keine Dokumente.</div>
                              <button class="subtle top-margined">
                                  <div class="center-aligning horizontal layout" style="width: 11.5em">
                                      <ptc-ui-icon name="icon.action.upload" class="huge"></ptc-ui-icon>
                                      <div class="tiny stretch left-margined text-left-aligning">
                                          Datei wählen oder per <strong>Drag & Drop</strong> hier ablegen.
                                      </div>
                                  </div>
                              </button>
                              ${this._renderNewDocumentOptions()}
                          </div>
                      `
                    : html`
                          <ptc-scroller class="stretch">
                              ${this._months.map(({ date, documents }) => {
                                  documents = documents.filter((document) => this._matchesFilters(document));
                                  return !documents.length
                                      ? null
                                      : html`
                                            <div class="small padded border-bottom subtle semibold sticky">
                                                ${formatMonth(date)}
                                            </div>
                                            ${documents.map(
                                                (document) => html`
                                                    <div
                                                        class="padded click horizontal center-aligning layout border-bottom document-row"
                                                        style="padding-left: 0;"
                                                        @click=${() => this._openDocument(document)}
                                                    >
                                                        <ptc-ui-icon
                                                            name="${fileIcon(document.type)}"
                                                            class="massive"
                                                        ></ptc-ui-icon>
                                                        <div class="stretch collapse">
                                                            <div class="small semibold ellipsis bottom-margined">
                                                                ${document.name}
                                                            </div>
                                                            <div class="tiny horizontal spacing layout">
                                                                <div class="pill">
                                                                    <ptc-ui-icon name="icon.object.date"></ptc-ui-icon>
                                                                    ${formatDate(document.date, false)}
                                                                </div>
                                                                <ptc-document-origin-pill
                                                                    .document=${document}
                                                                ></ptc-document-origin-pill>
                                                                ${document.hideFromEmployee
                                                                    ? html`
                                                                          <div class="pill">
                                                                              <ptc-ui-icon
                                                                                  name="icon.toggle.visibility-hidden"
                                                                              ></ptc-ui-icon>
                                                                          </div>
                                                                      `
                                                                    : ""}
                                                                ${document.tags.map(
                                                                    (tag) => html`
                                                                        <div
                                                                            class="pill ellipsis"
                                                                            style="max-width: 100%; --color-highlight: ${tag.color}"
                                                                        >
                                                                            <ptc-ui-icon
                                                                                name="icon.object.tag"
                                                                            ></ptc-ui-icon>
                                                                            ${tag.name}
                                                                        </div>
                                                                    `
                                                                )}
                                                                ${document.form
                                                                    ? html`<div
                                                                          class="pill"
                                                                          style="--color-highlight: ${formStatusColor(
                                                                              document.form.status
                                                                          )}"
                                                                      >
                                                                          <ptc-ui-icon
                                                                              name="icon.object.form"
                                                                          ></ptc-ui-icon>
                                                                          ${formStatusLabel(document.form.status)}
                                                                      </div>`
                                                                    : ""}
                                                            </div>
                                                        </div>
                                                        <div
                                                            class="horizontal layout document-actions"
                                                            @click=${(e: MouseEvent) => e.stopPropagation()}
                                                        >
                                                            <button
                                                                class="slim transparent"
                                                                @click=${() => this._download(document)}
                                                                ?disabled=${!document.url}
                                                            >
                                                                <ptc-ui-icon name="icon.action.download"></ptc-ui-icon>
                                                            </button>
                                                            <button
                                                                class="slim transparent"
                                                                @click=${() => this._edit(document)}
                                                                ?hidden=${!app.hasPermission(
                                                                    "manage.employees.documents"
                                                                )}
                                                            >
                                                                <ptc-ui-icon name="icon.action.edit"></ptc-ui-icon>
                                                            </button>
                                                            <button
                                                                class="slim transparent"
                                                                @click=${() => this._delete(document)}
                                                                ?hidden=${!app.hasPermission(
                                                                    "manage.employees.documents"
                                                                )}
                                                            >
                                                                <ptc-ui-icon name="icon.action.delete"></ptc-ui-icon>
                                                            </button>
                                                        </div>
                                                    </div>
                                                `
                                            )}
                                        `;
                              })}
                          </ptc-scroller>
                      `}
            </div>

            <div class="fullbleed center-aligning end-justifying vertical layout non-interactive">
                <div class="padded double-margined subtle box" style="background-color: white;">
                    <strong><ptc-ui-icon name="icon.toggle.suggestions"></ptc-ui-icon> Tipp:</strong> Sie können Dateien
                    einfach per Drag & Drop in dieses Fenster ziehen, um ein neues Dokument zu erstellen.
                </div>
            </div>

            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>
        `;
    }
}
